import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BillingTerms } from "@/components/onboarding/BillingTerms";
import { ActivateCard } from "@/components/ActivateCard";
import { CreditCard, Loader2 } from "lucide-react";

const LABEL = {
  not_started: ["Not set up", "bg-slate-700 text-slate-200 border-slate-600"],
  pending_activation: ["Pending Activation", "bg-sky-500/15 text-sky-300 border-sky-500/30"],
  active: ["Active", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"],
  past_due: ["Payment failed", "bg-rose-500/15 text-rose-300 border-rose-500/30"],
  canceled: ["Canceled", "bg-slate-700 text-slate-300 border-slate-600"],
  incomplete: ["Incomplete", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
};

export default function Billing() {
  const { role } = useAuth();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const sessionId = params.get("session_id");

  useEffect(() => {
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/checkout-status/${sessionId}`);
        setB(r.data.billing);
        if (r.data.payment_status !== "paid" && ++tries < 10 && !stop) setTimeout(poll, 2000);
      } catch { setB(null); }
    };
    if (sessionId) poll(); else api.get("/billing/status").then((r) => setB(r.data)).catch(() => {});
    return () => { stop = true; };
  }, [sessionId]);

  const portal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { setBusy(false); toast.error(e?.response?.data?.detail || "Could not open billing portal"); }
  };

  if (!b) return <div className="p-8 text-slate-400" data-testid="billing-loading">Loading…</div>;
  const [label, cls] = LABEL[b.status] || [b.status, LABEL.not_started[1]];
  const canManage = ["owner", "admin"].includes(role);
  return (
    <div className="p-6 lg:p-10 max-w-4xl space-y-6" data-testid="billing-page">
      <h1 className="font-display text-4xl sm:text-5xl font-bold">Billing</h1>
      <Card className="bg-slate-900 border-slate-800 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge className={`border text-sm px-3 py-1 ${cls}`} data-testid="billing-status">{label}</Badge>
          {b.setup_fee_paid && <span className="text-sm text-emerald-300" data-testid="billing-setup-paid">Setup fee paid</span>}
        </div>
        {b.status === "pending_activation" && (
          <div className="mt-3 space-y-1">
            <p className="text-emerald-300 font-semibold" data-testid="billing-payment-success">Payment Successful — Pending Activation</p>
            <p className="text-sm text-slate-300">Your ${b.initial_payment?.toLocaleString()} payment covers setup and your first month. No further charges until your account is activated; your next ${b.monthly_price} charge is one month after activation.</p>
          </div>
        )}
        {b.status === "active" && b.current_period_end && <p className="text-sm text-slate-300 mt-3" data-testid="billing-next-charge">Next charge on {new Date(b.current_period_end).toLocaleDateString()}.</p>}
        {b.last_payment_error && <p className="text-sm text-rose-300 mt-3" data-testid="billing-payment-error">{b.last_payment_error}. Update your card in the billing portal.</p>}
        <div className="flex flex-wrap gap-3 mt-5">
          {b.status === "not_started" && canManage && <Button asChild className="bg-orange-500 hover:bg-orange-600 text-slate-900 rounded-full" data-testid="billing-go-onboarding"><Link to="/app/onboarding?step=billing">Set up billing</Link></Button>}
          {b.has_customer && canManage && (
            <Button onClick={portal} disabled={busy} variant="outline" className="border-slate-700 rounded-full" data-testid="billing-open-portal">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />} Manage billing
            </Button>
          )}
        </div>
      </Card>
      {b.status === "pending_activation" && canManage && <ActivateCard onDone={setB} />}
      <BillingTerms billing={b} />
    </div>
  );
}
